import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

const LanguageContext = createContext(null);

const translations = {
  fr: {
    nav: {
      work: "Réalisations",
      services: "Services",
      collective: "Collectif",
      process: "Méthode",
      book: "Réserver",
      contact: "Contact",
    },
    hero: {
      eyebrow: "Agence Audiovisuelle, Paris, depuis 2017",
      headline_1: "Transformez vos pensées",
      headline_2: "en images.",
      sub: "Salside.Production est un collectif créatif de 33 talents : caméramans, photographes, monteurs, directeurs artistiques, au service des marques, des artistes et des événements à travers le monde.",
      cta_book: "Réserver une production",
      cta_work: "Voir nos réalisations",
      cta_quote: "Demander un devis",
      scroll: "Défilez",
    },
    about: {
      label: "01 À propos",
      title: "Une agence parisienne. Un réseau international.",
      p1: "Fondée en 2017 à Paris, Salside.Production fait partie du Salside Group. Nous sommes nés d'une conviction simple : l'image est devenue le langage le plus puissant pour raconter une marque, un artiste, un moment.",
      p2: "Nous orchestrons un réseau de prestataires experts dans leur domaine, du concept au tournage, du montage à la livraison, jusqu'à la publication. Chaque projet est un sur-mesure.",
      stats: [
        { value: "33", label: "Talents créatifs" },
        { value: "2017", label: "Année de création" },
        { value: "∞", label: "Possibilités" },
      ],
    },
    collective: {
      label: "02 Le Collectif",
      title: "33 créatifs.",
      title_2: "Une seule signature.",
      sub: "Notre force réside dans la diversité de nos métiers et la cohérence de notre direction artistique.",
      roles: [
        { count: "12", role: "Caméramans", detail: "Captation cinéma, événementiel, broadcast" },
        { count: "14", role: "Photographes", detail: "Mode, portrait, lifestyle, corporate" },
        { count: "04", role: "Monteurs & Graphistes", detail: "Post-production, motion design, identité" },
        { count: "03", role: "Directeurs Artistiques", detail: "Concept, direction de marque, narration" },
      ],
      marquee: ["Cinematographers", "Photographers", "Editors", "Graphic Designers", "Artistic Directors", "Producers", "Stylists", "Colorists"],
    },
    services: {
      label: "03 Services",
      title: "Ce que nous fabriquons.",
      sub: "Du concept à la diffusion, dix expertises au service de votre image.",
      list: [
        { num: "01", name: "Photographie", desc: "Mode, portrait, produit, lifestyle, événementiel" },
        { num: "02", name: "Film & Réalisation", desc: "Court-métrage, clip, brand film, documentaire" },
        { num: "03", name: "Couverture Événement", desc: "Soirées, lancements, tournées, festivals" },
        { num: "04", name: "Brand Content", desc: "Campagnes intégrées, séries, narratifs de marque" },
        { num: "05", name: "Lifestyle Content", desc: "Voyage, gastronomie, art de vivre, luxe" },
        { num: "06", name: "Social Media", desc: "Reels, TikTok, formats courts à fort impact" },
        { num: "07", name: "Visuels Business", desc: "Portraits dirigeants, locaux, journées corporate" },
        { num: "08", name: "Vidéos Corporate", desc: "Films institutionnels, RH, présentations" },
        { num: "09", name: "Montage", desc: "Post-production, étalonnage, mixage son" },
        { num: "10", name: "Direction Artistique", desc: "Concept, moodboard, identité visuelle" },
      ],
    },
    portfolio: {
      label: "04 Réalisations",
      title: "Travaux récents.",
      categories: ["Tous", "Events", "Brands", "Lifestyle", "Business", "Artists", "Luxury", "Travel"],
      empty: "Notre nouveau book arrive bientôt. En attendant, suivez-nous sur Instagram pour découvrir nos productions en temps réel.",
      visit_ig: "Voir sur Instagram",
    },
    trusted: {
      label: "Confiance",
      title: "Ils nous ont fait confiance.",
      sub: "Marques de luxe, institutions, médias, labels et événements internationaux.",
    },
    process: {
      label: "05 Méthode",
      title: "Comment nous travaillons.",
      steps: [
        { num: "01", name: "Réservation", desc: "Vous nous partagez votre vision. Nous étudions le projet et confirmons une équipe sur-mesure." },
        { num: "02", name: "Tournage / Shooting", desc: "Notre équipe capte avec précision, créativité et exigence technique." },
        { num: "03", name: "Montage", desc: "Post-production : narration, étalonnage, mixage. Chaque seconde compte." },
        { num: "04", name: "Livraison & Publication", desc: "Livraison aux formats requis et accompagnement à la diffusion." },
      ],
    },
    booking: {
      label: "06 Réservation",
      title: "Lançons votre projet.",
      sub: "Quelques questions pour cadrer votre besoin. Nous revenons vers vous sous 24h ouvrées.",
      fields: {
        name: "Nom complet",
        email: "Email",
        phone: "Téléphone",
        company: "Société / Marque",
        project_type: "Type de projet",
        location: "Lieu",
        project_date: "Date souhaitée",
        budget: "Budget estimé",
        deliverables: "Livrables souhaités",
        urgency: "Urgence",
        message: "Décrivez votre vision",
        references: "Références (URLs, moodboard...)",
      },
      project_types: ["Photo", "Film", "Événement", "Brand Content", "Lifestyle", "Social Media", "Corporate", "Autre"],
      urgencies: ["Flexible", "Sous 1 mois", "Sous 2 semaines", "Urgent (-1 semaine)"],
      budgets: ["< 1 000 €", "1 000 - 2 000 €", "2 000 - 5 000 €", "5 000 - 15 000 €", "15 000 - 50 000 €", "> 50 000 €", "À définir"],
      deliverables: ["Photos", "Vidéo longue", "Reels / TikTok", "Stories", "Print", "Web", "Identité graphique"],
      submit: "Envoyer la demande",
      sending: "Envoi…",
      success: "Merci. Nous revenons vers vous très vite.",
      error: "Une erreur est survenue. Réessayez ou écrivez-nous directement.",
    },
    contact: {
      label: "07 Contact",
      title: "Travaillons ensemble.",
      sub: "Une idée, une marque, un événement, un artiste à mettre en lumière ? Écrivez-nous.",
      ig: "Instagram",
      wa: "WhatsApp",
      email: "Email",
      paris: "Paris, Worldwide",
    },
    footer: {
      tagline: "Transform Your Thoughts Into Images.",
      rights: "Tous droits réservés.",
      group: "Membre du Salside Group",
    },
    admin: {
      login_title: "Espace Admin",
      email: "Email",
      password: "Mot de passe",
      sign_in: "Se connecter",
      logout: "Déconnexion",
      requests: "Demandes",
      portfolio: "Portfolio",
      upload: "Téléverser",
      title_field: "Titre",
      category_field: "Catégorie",
      file: "Fichier (image / vidéo)",
      delete: "Supprimer",
      no_requests: "Aucune demande pour le moment.",
      status: "Statut",
      mark_done: "Marquer traité",
      reopen: "Rouvrir",
      clients: "Clients",
      client_name: "Nom du client",
      client_logo: "Logo (PNG transparent recommandé)",
    },
  },
  en: {
    nav: {
      work: "Work",
      services: "Services",
      collective: "Collective",
      process: "Process",
      book: "Book",
      contact: "Contact",
    },
    hero: {
      eyebrow: "Audiovisual Agency, Paris, since 2017",
      headline_1: "Transform Your Thoughts",
      headline_2: "Into Images.",
      sub: "Salside.Production is a creative collective of 33 talents: cinematographers, photographers, editors, art directors, serving brands, artists and events worldwide.",
      cta_book: "Book a Production",
      cta_work: "View Our Work",
      cta_quote: "Request a Quote",
      scroll: "Scroll",
    },
    about: {
      label: "01 About",
      title: "A Paris agency. An international network.",
      p1: "Founded in 2017 in Paris, Salside.Production is part of Salside Group. We were born from one belief: the image is now the most powerful language to tell the story of a brand, an artist, a moment.",
      p2: "We orchestrate a network of expert providers, from concept to filming, from editing to delivery and publication. Every project is bespoke.",
      stats: [
        { value: "33", label: "Creative talents" },
        { value: "2017", label: "Founded" },
        { value: "∞", label: "Possibilities" },
      ],
    },
    collective: {
      label: "02 The Collective",
      title: "33 creatives.",
      title_2: "One signature.",
      sub: "Our strength lies in the breadth of our crafts and the consistency of our artistic direction.",
      roles: [
        { count: "12", role: "Cinematographers", detail: "Cinema, events, broadcast capture" },
        { count: "14", role: "Photographers", detail: "Fashion, portrait, lifestyle, corporate" },
        { count: "04", role: "Editors & Designers", detail: "Post-production, motion design, identity" },
        { count: "03", role: "Artistic Directors", detail: "Concept, brand direction, storytelling" },
      ],
      marquee: ["Cinematographers", "Photographers", "Editors", "Graphic Designers", "Artistic Directors", "Producers", "Stylists", "Colorists"],
    },
    services: {
      label: "03 Services",
      title: "What we make.",
      sub: "From concept to release, ten expertises at the service of your image.",
      list: [
        { num: "01", name: "Photography", desc: "Fashion, portrait, product, lifestyle, events" },
        { num: "02", name: "Film & Direction", desc: "Short film, music video, brand film, documentary" },
        { num: "03", name: "Event Coverage", desc: "Parties, launches, tours, festivals" },
        { num: "04", name: "Brand Content", desc: "Integrated campaigns, series, brand narratives" },
        { num: "05", name: "Lifestyle Content", desc: "Travel, food, art of living, luxury" },
        { num: "06", name: "Social Media", desc: "Reels, TikTok, high-impact short formats" },
        { num: "07", name: "Business Visuals", desc: "Executive portraits, offices, corporate days" },
        { num: "08", name: "Corporate Videos", desc: "Institutional films, HR, presentations" },
        { num: "09", name: "Editing", desc: "Post-production, color grading, sound mixing" },
        { num: "10", name: "Art Direction", desc: "Concept, moodboard, visual identity" },
      ],
    },
    portfolio: {
      label: "04 Work",
      title: "Recent productions.",
      categories: ["All", "Events", "Brands", "Lifestyle", "Business", "Artists", "Luxury", "Travel"],
      empty: "Our new book is on the way. Meanwhile, follow us on Instagram for our productions in real time.",
      visit_ig: "View on Instagram",
    },
    trusted: {
      label: "Trusted",
      title: "They trusted us.",
      sub: "Luxury brands, institutions, media, labels and international events.",
    },
    process: {
      label: "05 Process",
      title: "How we work.",
      steps: [
        { num: "01", name: "Reservation", desc: "You share your vision. We scope the project and assemble a bespoke team." },
        { num: "02", name: "Filming / Shooting", desc: "Our crew captures with precision, creativity and technical excellence." },
        { num: "03", name: "Editing", desc: "Post-production: storytelling, color, mix. Every second matters." },
        { num: "04", name: "Delivery & Publication", desc: "Delivery in all required formats and release support." },
      ],
    },
    booking: {
      label: "06 Book",
      title: "Let's start your project.",
      sub: "A few questions to scope your needs. We get back to you within 24 working hours.",
      fields: {
        name: "Full name",
        email: "Email",
        phone: "Phone",
        company: "Company / Brand",
        project_type: "Project type",
        location: "Location",
        project_date: "Desired date",
        budget: "Estimated budget",
        deliverables: "Desired deliverables",
        urgency: "Urgency",
        message: "Describe your vision",
        references: "References (URLs, moodboard...)",
      },
      project_types: ["Photo", "Film", "Event", "Brand Content", "Lifestyle", "Social Media", "Corporate", "Other"],
      urgencies: ["Flexible", "Within 1 month", "Within 2 weeks", "Urgent (under 1 week)"],
      budgets: ["< €1,000", "€1,000 - 2,000", "€2,000 - 5,000", "€5,000 - 15,000", "€15,000 - 50,000", "> €50,000", "TBD"],
      deliverables: ["Photos", "Long-form video", "Reels / TikTok", "Stories", "Print", "Web", "Graphic identity"],
      submit: "Send request",
      sending: "Sending…",
      success: "Thank you. We'll be in touch very soon.",
      error: "Something went wrong. Please retry or email us directly.",
    },
    contact: {
      label: "07 Contact",
      title: "Let's create together.",
      sub: "A brief, a brand, an event, an artist to bring to light? Drop us a line.",
      ig: "Instagram",
      wa: "WhatsApp",
      email: "Email",
      paris: "Paris, Worldwide",
    },
    footer: {
      tagline: "Transform Your Thoughts Into Images.",
      rights: "All rights reserved.",
      group: "Part of Salside Group",
    },
    admin: {
      login_title: "Admin Area",
      email: "Email",
      password: "Password",
      sign_in: "Sign in",
      logout: "Logout",
      requests: "Requests",
      portfolio: "Portfolio",
      upload: "Upload",
      title_field: "Title",
      category_field: "Category",
      file: "File (image / video)",
      delete: "Delete",
      no_requests: "No requests yet.",
      status: "Status",
      mark_done: "Mark handled",
      reopen: "Reopen",
    },
  },
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("salside_lang") || "fr");

  useEffect(() => {
    localStorage.setItem("salside_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const toggle = useCallback(() => {
    setLang((l) => (l === "fr" ? "en" : "fr"));
  }, []);

  const t = translations[lang];

  const value = useMemo(() => ({ lang, setLang, toggle, t }), [lang, toggle, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used inside LanguageProvider");
  return ctx;
};
